'use client';

import { Button as RadixButton, Box } from '@radix-ui/themes';
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { Link } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/link';
import { forwardRef, cloneElement, isValidElement } from 'react';

interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'outline' | 'soft' | 'light';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  withArrow?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isSelected?: boolean;
  isDark?: boolean;
  loading?: boolean;
  disabled?: boolean;
  color?: string;
  scrollToDumpsterTypes?: boolean;
  title?: string;
  'aria-label'?: string;
  'data-variable'?: string;
}

interface IconProps {
  color?: string;
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      href,
      onClick,
      variant = 'primary',
      size = 'small',
      className = '',
      withArrow = false,
      leftIcon,
      rightIcon,
      isSelected,
      isDark = false,
      loading = false,
      disabled = false,
      color,
      scrollToDumpsterTypes = false,
      title,
      'aria-label': ariaLabel,
      'data-variable': dataVariable,
    },
    ref,
  ) => {
    const getTextColorClass = () => {
      if (disabled) return 'text-gray-400';
      if (variant === 'primary') {
        return isDark
          ? color
            ? `text-[${color}]`
            : 'text-[var(--primary-color)]'
          : 'text-white';
      }
      if (variant === 'outline') {
        return isDark
          ? 'text-white'
          : color
          ? `text-[${color}]`
          : 'text-[var(--primary-color)]';
      }
      if (variant === 'soft') {
        return isSelected ? 'text-[var(--primary-color)]' : 'text-[#111827]';
      }
      if (variant === 'light') {
        return 'text-[var(--primary-color)]';
      }
    };

    const getHoverTextColorClass = () => {
      if (disabled) return '';
      if (variant === 'primary') return 'group-hover:text-white';
      if (variant === 'outline') {
        return isDark
          ? 'group-hover:text-[var(--primary-color)]'
          : color
          ? `group-hover:text-[${color}]`
          : 'group-hover:text-white';
      }
      return color
        ? `group-hover:text-[${color}]`
        : 'group-hover:text-[var(--primary-color)]';
    };

    const renderIcon = (icon: React.ReactNode) => {
      if (!icon) return null;
      if (isValidElement<IconProps>(icon)) {
        return cloneElement(icon, {
          color: 'currentColor',
          className: `transition-colors duration-200 ${getTextColorClass()} ${getHoverTextColorClass()} ${
            icon.props.className || ''
          }`,
        });
      }
      return icon;
    };

    const baseStyles = `
      group
      relative inline-flex items-center justify-center
      transition-all duration-200
      ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
      ${getTextColorClass()}
      ${variant === 'soft' && !isSelected ? getHoverTextColorClass() : ''}
      ${
        variant !== 'soft'
          ? '[clip-path:polygon(10px_0,100%_0,100%_calc(100%-10px),calc(100%-10px)_100%,0_100%,0_10px)]'
          : ''
      }
    `;

    const sizeStyles = {
      small: 'px-4 py-1 text-base min-h-[42px]',
      medium: 'px-5 py-1.5 text-base min-h-[46px]',
      large: 'px-6 py-2 text-base min-h-[50px]',
    };

    const svgPath =
      'M14 0H203C206.314 0 209 2.68629 209 6V35.3056C209 37.0194 208.267 38.6515 206.986 39.7901L200 46L192.705 52.4845C191.607 53.4607 190.188 54 188.719 54H6C2.68629 54 0 51.3137 0 48V14L14 0Z';

    const getPrimaryBackgroundStyle = () => {
      if (disabled) return 'bg-gray-300';
      if (isDark) return 'bg-white';
      return color ? `bg-[${color}]` : 'bg-[var(--primary-color)]';
    };

    const getBorderColor = () => {
      if (disabled) return 'border-gray-300';
      if (isDark) return 'border-white';
      return color ? `border-[${color}]` : 'border-[var(--primary-color)]';
    };

    const variantStyles = {
      primary: `
        ${getPrimaryBackgroundStyle()}
        ${
          isDark
            ? color
              ? `text-[${color}]`
              : 'text-[var(--primary-color)]'
            : 'text-white'
        }
        font-[800]
        ${disabled ? '' : 'hover:bg-[var(--secondary-color)] hover:text-white'}
        transition-all
      `,
      outline: `
        relative
        bg-transparent 
        ${
          isDark
            ? 'text-white'
            : color
            ? `text-[${color}]`
            : 'text-[var(--primary-color)]'
        }
        ${getBorderColor()}
        ${
          disabled
            ? ''
            : isDark
            ? 'hover:bg-white hover:text-[var(--primary-color)]'
            : 'hover:bg-[var(--primary-color)] hover:text-white'
        }
        transition-all
      `,
      soft: `
        ${
          isSelected
            ? 'bg-[var(--background-color-light)] text-[var(--primary-color)]'
            : 'bg-transparent text-[#000]'
        }
        font-[600]
        transition-all
        ${
          disabled
            ? ''
            : 'hover:bg-[var(--background-color-light)] hover:text-[var(--primary-color)]'
        }
        rounded-md
      `,
      light: `
        bg-[var(--background-color-light)]
        ${color ? `text-[${color}]` : 'text-[var(--primary-color)]'}
        font-[600]
        transition-all
        ${disabled ? '' : 'hover:opacity-80'}
        rounded-md
      `,
    };

    const combinedClassName = `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`;

    const content = (
      <>
        {variant === 'outline' && (
          <svg
            className={`absolute inset-0 w-full h-full ${
              isDark
                ? 'text-white'
                : color
                ? `text-[${color}]`
                : 'text-[var(--primary-color)]'
            } group-hover:opacity-0 transition-opacity duration-200`}
            viewBox="0 0 209 54"
            fill="none"
            preserveAspectRatio="none"
          >
            <path
              d={svgPath}
              stroke="currentColor"
              strokeWidth="4"
              vectorEffect="non-scaling-stroke"
              shapeRendering="crispEdges"
            />
          </svg>
        )}
        <span className="relative flex flex-col items-start justify-center gap-1">
          <span className="flex items-center justify-center gap-2 whitespace-nowrap">
            {renderIcon(leftIcon)}
            {children}
            {rightIcon
              ? renderIcon(rightIcon)
              : withArrow && <ArrowRightIcon className="w-4 h-4" />}
          </span>
        </span>
      </>
    );

    if (href) {
      return (
        <Box data-variable={dataVariable}>
          <RadixButton asChild className={combinedClassName}>
            <Link href={href} title={title} aria-label={ariaLabel}>
              {content}
            </Link>
          </RadixButton>
        </Box>
      );
    }

    if (scrollToDumpsterTypes) {
      return (
        <Box data-variable={dataVariable}>
          <RadixButton asChild className={combinedClassName}>
            <Link
              href="/#services"
              title={title || 'View available services and pricing'}
              aria-label={
                ariaLabel ||
                'Navigate to services section to see available services and pricing'
              }
            >
              {content}
            </Link>
          </RadixButton>
        </Box>
      );
    }

    return (
      <Box data-variable={dataVariable}>
        <RadixButton
          ref={ref}
          onClick={onClick}
          className={combinedClassName}
          loading={loading}
          disabled={disabled}
        >
          {content}
        </RadixButton>
      </Box>
    );
  },
);

Button.displayName = 'Button';

export default Button;
